import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/utils'

type CardTone = 'default' | 'muted' | 'dark' | 'glass' | 'intelligence'
type CardPadding = 'none' | 'sm' | 'md' | 'lg'

const toneClass: Record<CardTone, string> = {
  default: 'ui-card-default',
  muted: 'ui-card-muted',
  dark: 'ui-card-dark',
  glass: 'glass-panel',
  intelligence: 'intelligence-panel',
}

const paddingClass: Record<CardPadding, string> = {
  none: 'ui-card-pad-none',
  sm: 'ui-card-pad-sm',
  md: 'ui-card-pad-md',
  lg: 'ui-card-pad-lg',
}

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone
  padding?: CardPadding
  interactive?: boolean
}

export function Card({
  children,
  className,
  tone = 'default',
  padding = 'md',
  interactive = false,
  ...props
}: CardProps) {
  return (
    <div className={cn('ui-card', toneClass[tone], paddingClass[padding], interactive && 'ui-card-interactive', className)} {...props}>
      {children}
    </div>
  )
}

export function CardHeader({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ui-card-header', className)} {...props}>{children}</div>
}

export function CardTitle({ children, className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('card-title', className)} {...props}>{children as ReactNode}</h3>
}

export function CardDescription({ children, className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('caption ui-card-description', className)} {...props}>{children}</p>
}

export function CardContent({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ui-card-content', className)} {...props}>{children}</div>
}

export function CardFooter({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ui-card-footer', className)} {...props}>{children}</div>
}
